import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from '@/services/api'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const token = ref(localStorage.getItem('token'))
  const redirectUrl = ref(null)
  const pendingApprovals = ref(0)

  const isAuthenticated = computed(() => !!token.value)

  const setUser = (userData) => {
    user.value = userData
  }

  const setToken = (authToken) => {
    token.value = authToken
    if (authToken) {
      localStorage.setItem('token', authToken)
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`
    } else {
      localStorage.removeItem('token')
      delete axios.defaults.headers.common['Authorization']
    }
  }

  const setRedirectUrl = (url) => {
    redirectUrl.value = url
  }

  const login = async (credentials) => {
    try {
      const response = await axios.post('/auth/login', credentials)
      const { user: userData, token: authToken } = response.data

      setUser(userData)
      setToken(authToken)

      // Fetch pending approvals if user is manager, director or admin
      if (['manager', 'director', 'administrator'].includes(userData.role)) {
        await fetchPendingApprovals()
      }

      return response.data
    } catch (error) {
      throw error
    }
  }

  const loginWithProvider = (provider) => {
    const providers = {
      azure: '/auth/azure',
      google: '/auth/google',
      facebook: '/auth/facebook'
    }

    const apiBase = import.meta.env.VITE_API_BASE_URL || '/api'
    window.location.href = `${apiBase}${providers[provider]}`
  }

  const handleOAuthCallback = async (provider, code) => {
    try {
      const response = await axios.post(`/auth/${provider}/callback`, {
        code,
        redirect_uri: `${window.location.origin}/auth/callback/${provider}`
      })

      const { user: userData, token: authToken } = response.data

      setUser(userData)
      setToken(authToken)

      if (['manager', 'director', 'administrator'].includes(userData.role)) {
        await fetchPendingApprovals()
      }

      return response.data
    } catch (error) {
      throw error
    }
  }

  const logout = async () => {
    try {
      await axios.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      setToken(null)
      pendingApprovals.value = 0
    }
  }

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get('/auth/me')
      setUser(response.data)

      if (['manager', 'director', 'administrator'].includes(response.data.role)) {
        await fetchPendingApprovals()
      }

      return response.data
    } catch (error) {
      setToken(null)
      throw error
    }
  }

  const fetchPendingApprovals = async () => {
    try {
      const response = await axios.get('/approvals/pending/count')
      pendingApprovals.value = response.data.count
    } catch (error) {
      console.error('Error fetching pending approvals:', error)
    }
  }

  // Initialize auth state
  const initAuth = () => {
    if (token.value) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
      fetchCurrentUser().catch(() => {
        setToken(null)
      })
    }
  }

  return {
    user,
    token,
    redirectUrl,
    pendingApprovals,
    isAuthenticated,
    setUser,
    setToken,
    setRedirectUrl,
    login,
    loginWithProvider,
    handleOAuthCallback,
    logout,
    fetchCurrentUser,
    fetchPendingApprovals,
    initAuth
  }
})